import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, Download, ArrowLeft, Droplets } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatJMD, fmtDate, PAYMENT_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";

const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export default function ManifestPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [run, setRun] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/deliveries").then((r) => setRun(r.data.find((d) => d.id === runId) || null)).catch(() => {});
  }, [user, runId]);

  if (loading) return null;
  if (!user) { navigate("/admin"); return null; }
  if (!run) return <div className="p-10 text-slate-500" data-testid="manifest-loading">Loading run sheet…</div>;

  const codTotal = run.orders.filter((o) => o.payment_method === "cod").reduce((a, o) => a + o.total_amount, 0);

  const downloadCsv = () => {
    const rows = [["Stop", "Order", "Customer", "Phone", "Address", "Items", "Payment", "Amount", "Status", "Notes"]];
    run.orders.forEach((o, i) => rows.push([
      i + 1, o.order_code, o.customer_name, o.phone, o.address,
      o.items.map((it) => `${it.quantity}x ${it.product_name}`).join("; "),
      PAYMENT_LABEL[o.payment_method], o.total_amount, o.status, o.notes || "",
    ]));
    const blob = new Blob([rows.map((r) => r.map(csvEscape).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `run-sheet-${run.run_date}-${run.driver.replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="manifest-page">
      <div className="print:hidden sticky top-0 bg-slate-100 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="manifest-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Logistics</Button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="manifest-download-csv" className="rounded-xl"><Download className="w-4 h-4 mr-1" /> CSV</Button>
            <Button size="sm" onClick={() => window.print()} data-testid="manifest-print" className="rounded-xl bg-sky-600 hover:bg-sky-500 text-white"><Printer className="w-4 h-4 mr-1" /> Print / Save PDF</Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-8 print:py-2">
        <div className="flex items-start justify-between border-b-2 border-sky-900 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-sky-900 flex items-center justify-center"><Droplets className="w-6 h-6 text-sky-200" /></div>
            <div>
              <div className="font-display text-2xl font-extrabold">Cool Waves Water</div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Daily Delivery Run Sheet</div>
            </div>
          </div>
          <div className="text-right text-sm">
            <div><span className="text-slate-500">Date:</span> <strong data-testid="manifest-date">{fmtDate(run.run_date)}</strong></div>
            <div><span className="text-slate-500">Driver:</span> <strong data-testid="manifest-driver">{run.driver}</strong></div>
            <div><span className="text-slate-500">Stops:</span> <strong>{run.orders.length}</strong></div>
          </div>
        </div>

        <table className="w-full mt-6 text-sm border-collapse" data-testid="manifest-table">
          <thead>
            <tr className="bg-slate-100 text-left text-xs uppercase tracking-wider text-slate-600">
              <th className="p-2 w-10">#</th><th className="p-2">Customer & Address</th><th className="p-2">Items</th><th className="p-2">Payment</th><th className="p-2 w-14 text-center">Done</th>
            </tr>
          </thead>
          <tbody>
            {run.orders.map((o, i) => (
              <tr key={o.id} className="border-b border-slate-200 align-top break-inside-avoid" data-testid={`manifest-stop-${i + 1}`}>
                <td className="p-2 font-bold text-sky-800">{i + 1}</td>
                <td className="p-2">
                  <div className="font-semibold">{o.customer_name} <span className="font-mono-cw text-xs text-slate-400 ml-1">{o.order_code}</span></div>
                  <div className="text-slate-700">{o.address}</div>
                  <div className="text-slate-500">{o.phone}</div>
                  {o.notes && <div className="text-xs italic text-amber-700 mt-1">Note: {o.notes}</div>}
                </td>
                <td className="p-2">{o.items.map((it, j) => <div key={j}>{it.quantity}× {it.product_name} <span className="text-slate-400 text-xs">({it.size})</span></div>)}</td>
                <td className="p-2">
                  <div className="font-mono-cw font-bold">{formatJMD(o.total_amount)}</div>
                  <div className={`text-xs font-semibold ${o.payment_method === "cod" ? "text-emerald-700" : "text-sky-700"}`}>{o.payment_method === "cod" ? "COLLECT CASH" : "Bank transfer"}</div>
                </td>
                <td className="p-2 text-center"><div className="w-6 h-6 border-2 border-slate-400 rounded mx-auto" /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="border-2 border-emerald-600 rounded-xl p-4 text-right">
            <div className="text-xs uppercase tracking-wider text-slate-500">Cash to collect (COD)</div>
            <div className="font-mono-cw text-2xl font-extrabold text-emerald-700" data-testid="manifest-cod-total">{formatJMD(codTotal)}</div>
          </div>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-10 text-sm text-slate-500">
          <div className="border-t border-slate-400 pt-2">Driver signature</div>
          <div className="border-t border-slate-400 pt-2">Checked by</div>
        </div>
      </div>
    </div>
  );
}
